/* eslint-disable @typescript-eslint/indent */
/* eslint-disable react/jsx-indent */
import { Box, Typography, Collapse, IconButton, styled } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { type AggregatedRow, type GSRPCapacityReportModel } from './GSRPCapacityReport.types'

// Styled components for repeated patterns
const StatsCard = styled(Box)({
  flex: 1,
  maxWidth: '140px',
  minWidth: '140px',
  padding: '12px',
  border: '1px solid #E1E1E1',
  borderRadius: '4px',
  backgroundColor: '#FAFAFA'
})

const CardHeader = styled(Typography)({
  fontSize: '12px',
  color: '#4A4A4A',
  fontWeight: 'bold',
  display: 'block',
  marginBottom: '8px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
})

const StatLabel = styled(Typography)({
  fontSize: '12px',
  color: '#4A4A4A'
})

const StatValue = styled(Typography)({
  fontSize: '13px',
  fontWeight: 'bold'
})

// Smaller cards for session-level details
const SessionStatsCard = styled(Box)({
  flex: 1,
  minWidth: '120px',
  padding: '8px',
  border: '1px solid #E1E1E1',
  borderRadius: '4px',
  backgroundColor: '#FAFAFA'
})

const SessionCardHeader = styled(Typography)({
  fontSize: '12px',
  color: '#4A4A4A',
  fontWeight: 'bold',
  display: 'block',
  marginBottom: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
})

const SessionStatLabel = styled(Typography)({
  fontSize: '12px',
  color: '#4A4A4A'
})

const SessionStatValue = styled(Typography)({
  fontSize: '12px',
  fontWeight: 'bold'
})

interface FacilityDetailsProps {
  facility: AggregatedRow
  providerName: string
  sessions?: GSRPCapacityReportModel[]
  isExpandable?: boolean
  isExpanded?: boolean
  isHighlighted?: boolean
  onExpandToggle?: () => void
  getCapacityStatus: (totalActive: number, capacity: number) => { label: string, color: string }
  showAddress?: boolean
  showProviderName?: boolean
  disableInteractive?: boolean
}

export function FacilityDetails (props: FacilityDetailsProps): JSX.Element {
  const {
    facility,
    providerName,
    sessions,
    isExpandable = false,
    isExpanded = false,
    isHighlighted = false,
    onExpandToggle,
    getCapacityStatus,
    showAddress = true,
    showProviderName = false,
    disableInteractive = false
  } = props

  // Calculate all capacity metrics
  const capacity = facility.totalAllocatedCapacity
  const active = facility.totalActiveSeats
  const openSeats = Math.max(0, capacity - active)
  const pending = facility.countPendingProcessing
  const waitlisted = facility.countWaitlisted
  const seeking = pending + waitlisted

  // Determine status
  const getDetailedStatus = (): { label: string, bgColor: string, textColor: string, message: string } => {
    const baseMessage = `${openSeats} open, ${seeking} seeking (${pending} pending + ${waitlisted} waitlisted)`

    if (openSeats === 0) {
      return {
        label: 'At Capacity',
        bgColor: '#E4536B',
        textColor: 'white',
        message: baseMessage
      }
    } else if (seeking > openSeats) {
      return {
        label: 'Limited Capacity',
        bgColor: '#C7A900',
        textColor: 'black',
        message: baseMessage
      }
    } else {
      return {
        label: 'Capacity Available',
        bgColor: '#367575',
        textColor: 'white',
        message: baseMessage
      }
    }
  }

  const status = getDetailedStatus()

  const copyAddress = (e: React.MouseEvent): void => {
    e.stopPropagation()
    void navigator.clipboard.writeText(facility.facilityAddress ?? '')
  }

  return (
    <Box
      sx={{
        borderLeft: isHighlighted ? '4px solid #722a82' : 'none',
        bgcolor: isHighlighted ? '#FDF7FF' : 'transparent',
        transition: 'all 0.3s ease',
        boxShadow: isHighlighted ? 'inset 0 0 10px rgba(114, 42, 130, 0.05)' : 'none'
      }}
    >
      <Box
        onClick={isExpandable ? onExpandToggle : undefined}
        sx={{
          p: 1.5,
          pl: showProviderName ? 1.5 : (isExpandable ? 5 : 1.5),
          borderBottom: '1px solid #E1E1E1',
          bgcolor: 'transparent',
          '&:hover': isExpandable && !showProviderName
            ? {
                bgcolor: '#E8E8E8',
                cursor: 'pointer'
              }
            : {}
        }}
      >
        {/* Provider name - show in map popup */}
        {showProviderName && (
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              fontSize: '14px',
              mb: 1,
              m: showProviderName ? 0 : undefined,
              lineHeight: showProviderName ? 1.5 : undefined
            }}
          >
            {providerName}
          </Typography>
        )}

        {/* Facility name */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1
          }}
        >
          {isExpandable && (
            isExpanded
              ? <ExpandMoreIcon fontSize="small" sx={{ fontSize: '18px' }} />
              : <ChevronRightIcon fontSize="small" sx={{ fontSize: '18px' }} />
          )}
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              fontSize: '14px',
              m: showProviderName ? 0 : undefined,
              lineHeight: showProviderName ? 1.5 : undefined
            }}
          >
            {facility.facilityName}
          </Typography>
        </Box>

        {/* Address with copy button */}
        {showAddress && (facility.facilityAddress !== undefined && facility.facilityAddress !== null && facility.facilityAddress !== '') && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 1,
              ml: showProviderName ? 0 : (isExpandable ? 3 : 0)
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: '#4A4A4A',
                fontSize: '12px',
                flex: 1,
                m: showProviderName ? 0 : undefined,
                lineHeight: showProviderName ? 1.5 : undefined
              }}
            >
              {facility.facilityAddress}
            </Typography>
            {disableInteractive
              ? (
              <button
                data-address={facility.facilityAddress}
                className="copy-address-btn"
                style={{
                  padding: '4px',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  border: 'none',
                  minWidth: 'auto',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Copy address"
                aria-label={`Copy address: ${facility.facilityAddress}`}
              >
                <ContentCopyIcon style={{ fontSize: '16px', display: 'block' }} />
              </button>
                )
              : (
              <IconButton
                size="small"
                onClick={copyAddress}
                data-address={facility.facilityAddress}
                className="copy-address-btn"
                aria-label={`Copy address: ${facility.facilityAddress}`}
                sx={{
                  p: 0.5,
                  cursor: showProviderName ? 'pointer' : undefined,
                  bgcolor: showProviderName ? 'transparent' : undefined,
                  border: showProviderName ? 'none' : undefined,
                  minWidth: showProviderName ? 'auto' : undefined,
                  '&:hover': !showProviderName
                    ? { color: '#722a82' }
                    : {}
                }}
                title="Copy address"
              >
                <ContentCopyIcon
                  sx={{
                    fontSize: '16px',
                    display: showProviderName ? 'block' : undefined
                  }}
                />
              </IconButton>
                )}
          </Box>
        )}

        {/* Capacity details */}
        <Box sx={{ ml: showProviderName ? 0 : (isExpandable ? 3 : 0) }}>
          {/* Two-column card layout */}
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              flexWrap: showProviderName ? 'nowrap' : 'wrap'
            }}
          >
            {/* Availability card */}
            <StatsCard sx={{ minWidth: showProviderName ? '140px' : undefined }}>
              <CardHeader
                variant="caption"
                sx={{
                  m: showProviderName ? '0 0 8px 0' : undefined,
                  lineHeight: showProviderName ? 1.5 : undefined
                }}
              >
                Availability
              </CardHeader>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <StatLabel
                    variant="caption"
                    sx={{
                      m: showProviderName ? 0 : undefined,
                      lineHeight: showProviderName ? 1.5 : undefined
                    }}
                  >
                    Total
                  </StatLabel>
                  <StatValue
                    variant="caption"
                    sx={{
                      m: showProviderName ? 0 : undefined,
                      lineHeight: showProviderName ? 1.5 : undefined
                    }}
                  >
                    {capacity} seats
                  </StatValue>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <StatLabel
                    variant="caption"
                    sx={{
                      m: showProviderName ? 0 : undefined,
                      lineHeight: showProviderName ? 1.5 : undefined
                    }}
                  >
                    Active
                  </StatLabel>
                  <StatValue
                    variant="caption"
                    sx={{
                      m: showProviderName ? 0 : undefined,
                      lineHeight: showProviderName ? 1.5 : undefined
                    }}
                  >
                    {active} enrolled
                  </StatValue>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    pt: 0.5,
                    borderTop: '1px solid #E1E1E1',
                    mt: showProviderName ? 0.5 : undefined
                  }}
                >
                  <StatLabel
                    variant="caption"
                    sx={{
                      m: showProviderName ? 0 : undefined,
                      lineHeight: showProviderName ? 1.5 : undefined
                    }}
                  >
                    Open
                  </StatLabel>
                  <StatValue
                    variant="caption"
                    sx={{
                      fontSize: '14px',
                      color: status.bgColor,
                      m: showProviderName ? 0 : undefined,
                      lineHeight: showProviderName ? 1.5 : undefined
                    }}
                  >
                    {openSeats} seats
                  </StatValue>
                </Box>
              </Box>
            </StatsCard>

            {/* Seeking Spots card */}
            <StatsCard>
              <CardHeader
                variant="caption"
                sx={{
                  m: showProviderName ? '0 0 8px 0' : undefined,
                  lineHeight: showProviderName ? 1.5 : undefined
                }}
              >
                Seeking Spots
              </CardHeader>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <StatLabel
                    variant="caption"
                    sx={{
                      m: showProviderName ? 0 : undefined,
                      lineHeight: showProviderName ? 1.5 : undefined
                    }}
                  >
                    Pending
                  </StatLabel>
                  <StatValue
                    variant="caption"
                    sx={{
                      m: showProviderName ? 0 : undefined,
                      lineHeight: showProviderName ? 1.5 : undefined
                    }}
                  >
                    {pending}
                  </StatValue>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <StatLabel
                    variant="caption"
                    sx={{
                      m: showProviderName ? 0 : undefined,
                      lineHeight: showProviderName ? 1.5 : undefined
                    }}
                  >
                    Waitlisted
                  </StatLabel>
                  <StatValue
                    variant="caption"
                    sx={{
                      m: showProviderName ? 0 : undefined,
                      lineHeight: showProviderName ? 1.5 : undefined
                    }}
                  >
                    {waitlisted}
                  </StatValue>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    pt: 0.5,
                    borderTop: '1px solid #E1E1E1',
                    mt: showProviderName ? 0.5 : undefined
                  }}
                >
                  <StatLabel
                    variant="caption"
                    sx={{
                      m: showProviderName ? 0 : undefined,
                      lineHeight: showProviderName ? 1.5 : undefined
                    }}
                  >
                    Total
                  </StatLabel>
                  <StatValue
                    variant="caption"
                    sx={{
                      fontSize: '14px',
                      color: '#E65100',
                      m: showProviderName ? 0 : undefined,
                      lineHeight: showProviderName ? 1.5 : undefined
                    }}
                  >
                    {seeking} seeking
                  </StatValue>
                </Box>
              </Box>
            </StatsCard>
          </Box>
        </Box>
      </Box>

      {/* Sessions - shown when facility is expanded */}
      {isExpandable && sessions !== undefined && (
        <Collapse in={isExpanded}>
          <Box sx={{ bgcolor: '#F5F5F5' }}>
            {sessions.map((session, sessionIndex) => {
              const sessionCapacity = session.totalAllocatedCapacity
              const sessionActive = session.totalActiveSeats
              const sessionOpenSeats = Math.max(0, sessionCapacity - sessionActive)
              const sessionPending = session.countPendingProcessing
              const sessionWaitlisted = session.countWaitlisted
              const sessionSeeking = sessionPending + sessionWaitlisted

              // Get session status with proper contrast
              const sessionStatusRaw = getCapacityStatus(sessionActive, sessionCapacity)
              const sessionOpenPercent = sessionCapacity === 0 ? 0 : (sessionActive / sessionCapacity) * 100
              const sessionStatus = {
                label: sessionStatusRaw.label,
                bgColor: sessionStatusRaw.color,
                textColor: sessionOpenPercent >= 80 && sessionOpenPercent < 100 ? 'black' : 'white'
              }

              return (
                <Box
                  key={sessionIndex}
                  sx={{
                    p: 2,
                    pl: 8,
                    borderBottom: '1px solid #E1E1E1',
                    bgcolor: 'white'
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '13px', display: 'block', mb: 1 }}>
                    {session.sessionName}
                  </Typography>
                  <Box>
                    {/* Two-column card layout for sessions */}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {/* Session availability card */}
                      <SessionStatsCard>
                        <SessionCardHeader variant="caption">
                          Availability
                        </SessionCardHeader>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <SessionStatLabel variant="caption">
                              Total
                            </SessionStatLabel>
                            <SessionStatValue variant="caption">
                              {sessionCapacity}
                            </SessionStatValue>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <SessionStatLabel variant="caption">
                              Active
                            </SessionStatLabel>
                            <SessionStatValue variant="caption">
                              {sessionActive}
                            </SessionStatValue>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.25, borderTop: '1px solid #E1E1E1' }}>
                            <SessionStatLabel variant="caption">
                              Open
                            </SessionStatLabel>
                            <SessionStatValue variant="caption" sx={{ color: sessionStatus.bgColor }}>
                              {sessionOpenSeats}
                            </SessionStatValue>
                          </Box>
                        </Box>
                      </SessionStatsCard>

                      {/* Session seeking spots card */}
                      <SessionStatsCard>
                        <SessionCardHeader variant="caption">
                          Seeking Spots
                        </SessionCardHeader>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <SessionStatLabel variant="caption">
                              Pending
                            </SessionStatLabel>
                            <SessionStatValue variant="caption">
                              {sessionPending}
                            </SessionStatValue>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <SessionStatLabel variant="caption">
                              Waitlisted
                            </SessionStatLabel>
                            <SessionStatValue variant="caption">
                              {sessionWaitlisted}
                            </SessionStatValue>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.25, borderTop: '1px solid #E1E1E1' }}>
                            <SessionStatLabel variant="caption">
                              Total
                            </SessionStatLabel>
                            <SessionStatValue variant="caption" sx={{ color: '#E65100' }}>
                              {sessionSeeking}
                            </SessionStatValue>
                          </Box>
                        </Box>
                      </SessionStatsCard>
                    </Box>
                  </Box>
                </Box>
              )
            })}
          </Box>
        </Collapse>
      )}
    </Box>
  )
}
